import type { Plan, RunState } from '../v4/contracts'

/**
 * A run as one growing flowchart across its plan versions. The engine keeps the steps that ran in
 * every later version, an unchanged step keeps its id, and a new step gets a fresh one, so the
 * union of the versions' steps by id is the run's whole flow. A version is drawn when it was
 * approved, and the latest always is; a version replaced before anyone approved it is a draft
 * nobody ran and leaves nothing behind. Everything here is read from the run: the versions, their
 * approvals, the steps' trace and the `replanned` event's `cause`.
 */

type PlanStep = Plan['steps'][number]

/** What made a version: a step's finding (with a file that was waiting), a file the engineer sent, their edit, or not recorded. */
export type VersionCause =
  | { kind: 'step'; stepId: string; file?: true }
  | { kind: 'file' }
  | { kind: 'edit' }
  | { kind: 'unknown' }

/** What the step behind a version found, from its evidence and trace: part of its period, nothing, or a failure. */
export type Finding =
  | { kind: 'coverage'; observed: number; expected: number }
  | { kind: 'empty' }
  | { kind: 'failed' }
  | null

export interface VersionMark {
  version: number
  /** The drawn version it follows. */
  after: number
  cause: VersionCause
  finding: Finding
  /** The model's words for why, or the engineer's edit reason. */
  reason: string | null
  /** Steps this version added, in plan order. */
  added: string[]
  /** Steps of the version before that never ran and this version left out. */
  dropped: string[]
}

export interface FlowStep {
  step: PlanStep
  /** The latest drawn version that holds the step: where its status and details are read. */
  plan: Plan
  /** The drawn version the step first appears in. */
  introducedIn: number
  /** The drawn version that left the step out before it ran. */
  replacedIn?: number
}

export interface RunFlow {
  /** Every drawn step, first-seen order. */
  steps: FlowStep[]
  /** One mark per drawn version after the first. */
  marks: VersionMark[]
}

/** The versions the canvas draws: those approved, and the latest. */
export function drawnVersions(run: RunState): Plan[] {
  const all = run.trace.planVersions
  const last = all.at(-1)
  const approved = new Set(run.events.filter((e) => e.type === 'plan_approved').map((e) => e.planVersion))
  return all.filter((p) => p === last || approved.has(p.version))
}

function causeOf(run: RunState, version: number): VersionCause {
  const proposed = run.events.find((e) => e.type === 'plan_proposed' && e.planVersion === version)
  if ((proposed?.payload as { edited?: unknown } | undefined)?.edited === true) return { kind: 'edit' }
  const replanned = run.events.find((e) => e.type === 'replanned' && e.planVersion === version)
  const cause = (replanned?.payload as { cause?: VersionCause } | undefined)?.cause
  return cause && (cause.kind === 'step' || cause.kind === 'file') ? cause : { kind: 'unknown' }
}

function findingOf(run: RunState, stepId: string): Finding {
  const traced = run.trace.steps.find((s) => s.stepId === stepId)
  if (!traced) return null
  const evidence = run.trace.evidence.filter((e) => traced.evidenceIds?.includes(e.id))
  const covered = evidence.find((e) => e.coverage && e.coverage.observed < e.coverage.expected)?.coverage
  if (covered) return { kind: 'coverage', observed: covered.observed, expected: covered.expected }
  if (traced.outcome === 'empty') return { kind: 'empty' }
  if (traced.outcome === 'error' || traced.outcome === 'unavailable' || traced.outcome === 'failed') return { kind: 'failed' }
  return null
}

const ran = (run: RunState, stepId: string) => run.trace.steps.some((s) => s.stepId === stepId)

/** The run's flow across its drawn versions; empty before a plan exists. */
export function runHistory(run: RunState | null): RunFlow {
  if (!run) return { steps: [], marks: [] }
  const drawn = drawnVersions(run)
  const byId = new Map<string, FlowStep>()
  const marks: VersionMark[] = []
  drawn.forEach((plan, i) => {
    const before = drawn[i - 1]
    const added: string[] = []
    for (const step of plan.steps) {
      const seen = byId.get(step.id)
      if (seen) byId.set(step.id, { ...seen, step, plan })
      else {
        byId.set(step.id, { step, plan, introducedIn: plan.version })
        added.push(step.id)
      }
    }
    if (!before) return
    const here = new Set(plan.steps.map((s) => s.id))
    const dropped = before.steps.filter((s) => !here.has(s.id) && !ran(run, s.id)).map((s) => s.id)
    for (const id of dropped) byId.set(id, { ...byId.get(id)!, replacedIn: plan.version })
    // The version that made the change is the first one after the drawn version before it: later drafts only edited it.
    const first = run.trace.planVersions.find((p) => p.version > before.version) ?? plan
    const cause = causeOf(run, first.version)
    marks.push({
      version: plan.version,
      after: before.version,
      cause,
      finding: cause.kind === 'step' ? findingOf(run, cause.stepId) : null,
      reason: first.reason ?? null,
      added,
      dropped,
    })
  })
  return { steps: [...byId.values()], marks }
}

/** The plan version a step's details are read from: the latest drawn version that holds it. */
export function ownerPlan(run: RunState, stepId: string): Plan | null {
  return runHistory(run).steps.find((f) => f.step.id === stepId)?.plan ?? null
}
